'use client'

import Image from 'next/image'
import * as React from 'react'

import type { ImageBuilderProps, SanityImage } from '@/constant/types'
import { cn } from '@/lib/utils'
import { urlForImage } from '@/sanity/lib/image'

type PhotoElementProps = {
  image: SanityImage
  imageProps?: ImageBuilderProps
  fill?: boolean
  priority?: boolean
  placeholder?: 'empty' | 'blur'
  altText?: string
  sizes?: string
  className?: string
  style?: React.CSSProperties
}

export default function PhotoElement({
  image,
  imageProps,
  fill = false,
  priority = false,
  placeholder = 'empty',
  altText,
  sizes = '100vw',
  className,
  style = {},
}: PhotoElementProps) {
  const imageWidth =
    imageProps?.width || (image?.asset?.metadata?.dimensions?.width as number)
  const imageHeight =
    imageProps?.height || (image?.asset?.metadata?.dimensions?.height as number)

  const width = fill ? undefined : imageWidth
  const height = fill ? undefined : imageHeight

  const placeholderStyle = {
    ...style,
    position: 'absolute' as const,
    zIndex: 2,
    width: '100%',
    height: '100%',
    opacity: 0.4,
    top: 0,
    left: 0,
    filter: 'blur(20px)',
  }

  const [isLoaded, setIsLoaded] = React.useState(false)

  return (
    <>
      {fill && !isLoaded && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          width={imageWidth}
          height={imageHeight}
          className={cn(className, '')}
          src={image?.asset?.metadata?.lqip}
          alt={altText || 'A photo'}
          style={{
            ...placeholderStyle,
          }}
        />
      )}
      <Image
        className={cn('', fill && !isLoaded && 'opacity-0', className)}
        src={urlForImage(image, imageProps).url()}
        width={width}
        height={height}
        sizes={sizes}
        alt={altText || 'A photo'}
        fill={fill}
        blurDataURL={image?.asset?.metadata?.lqip}
        style={style}
        priority={priority}
        placeholder={placeholder}
        onLoad={() => setIsLoaded(true)}
      />
    </>
  )
}
